/**
 * Skeleton Component
 * Loading placeholder with pulsing animation
 */

export type SkeletonVariant = 'track' | 'playlist' | 'chart' | 'text' | 'circle' | 'rectangle';

export interface SkeletonProps {
  variant?: SkeletonVariant;
  count?: number;
  className?: string;
  width?: string;
  height?: string;
}

/**
 * Base skeleton element with pulse animation
 */
function SkeletonBase({
  className = '',
  width = 'w-full',
  height = 'h-4',
}: {
  className?: string;
  width?: string;
  height?: string;
}) {
  return (
    <div
      className={`
        ${width} ${height}
        bg-zinc-800 rounded
        animate-pulse
        ${className}
      `}
    />
  );
}

/**
 * Skeleton for a track row
 */
function SkeletonTrack() {
  return (
    <div className="flex items-center gap-3 p-3 border-b border-zinc-800">
      {/* Album Art */}
      <SkeletonBase width="w-12" height="h-12" className="rounded-md" />

      {/* Track Info */}
      <div className="flex-1 space-y-2">
        <SkeletonBase width="w-3/4" height="h-4" />
        <SkeletonBase width="w-1/2" height="h-3" />
      </div>

      {/* Duration */}
      <SkeletonBase width="w-12" height="h-3" />

      {/* Actions */}
      <SkeletonBase width="w-6" height="h-6" className="rounded-full" />
    </div>
  );
}

/**
 * Skeleton for a playlist item
 */
function SkeletonPlaylist() {
  return (
    <div className="p-4 border border-zinc-800 rounded-lg">
      {/* Cover Image */}
      <SkeletonBase width="w-full" height="h-40" className="rounded-md mb-3" />

      {/* Playlist Name */}
      <SkeletonBase width="w-3/4" height="h-5" className="mb-2" />

      {/* Track Count */}
      <SkeletonBase width="w-1/2" height="h-3" />
    </div>
  );
}

/**
 * Skeleton for a chart/graph
 */
function SkeletonChart() {
  return (
    <div className="p-6 border border-zinc-800 rounded-lg">
      {/* Title */}
      <SkeletonBase width="w-1/3" height="h-6" className="mb-6" />

      {/* Chart Area */}
      <div className="space-y-3">
        {[...Array(5)].map((_, i) => (
          <div key={i} className="flex items-end gap-2" style={{ height: '120px' }}>
            {[...Array(8)].map((_, j) => (
              <SkeletonBase
                key={j}
                width="flex-1"
                height={`h-[${Math.random() * 80 + 20}px]`}
                className="rounded-t"
              />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Main Skeleton component
 */
export function Skeleton({
  variant = 'rectangle',
  count = 1,
  className = '',
  width,
  height,
}: SkeletonProps) {
  // Render specific variant multiple times
  const items = [...Array(count)].map((_, i) => {
    switch (variant) {
      case 'track':
        return <SkeletonTrack key={i} />;

      case 'playlist':
        return <SkeletonPlaylist key={i} />;

      case 'chart':
        return <SkeletonChart key={i} />;

      case 'circle':
        return (
          <SkeletonBase
            key={i}
            width={width || 'w-12'}
            height={height || 'h-12'}
            className={`rounded-full ${className}`}
          />
        );

      case 'text':
        return (
          <SkeletonBase
            key={i}
            width={width || 'w-full'}
            height={height || 'h-4'}
            className={className}
          />
        );

      case 'rectangle':
      default:
        return (
          <SkeletonBase
            key={i}
            width={width || 'w-full'}
            height={height || 'h-24'}
            className={className}
          />
        );
    }
  });

  return <>{items}</>;
}

/**
 * Skeleton group for lists
 */
export interface SkeletonGroupProps {
  variant: SkeletonVariant;
  count: number;
  className?: string;
}

export function SkeletonGroup({ variant, count, className = '' }: SkeletonGroupProps) {
  return (
    <div className={`space-y-3 ${className}`}>
      <Skeleton variant={variant} count={count} />
    </div>
  );
}
